/**
 * DriverSiteInfo — integrated per-booking site-info page (Phase F δ).
 *
 * Route: /d/:token   (no auth required; token is unguessable)
 *
 * Combines:
 *   • Booking header (ref, time, direction)
 *   • Site rules sections   (incl. is_internal — site map, depot PDFs …)
 *
 * This page is intentionally `noindex` — the URL is shared with drivers /
 * bookers via the confirmation email and is the integrated view they need
 * to navigate the site safely.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import DriverSiteTime from "@/components/DriverSiteTime";
import { placeTimeLabel, siteTz } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import {
  Truck, AlertCircle, Clock, Mail, Phone, MapPin, Share2, Printer,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import SectionRenderer from "@/components/SectionRenderer";
import DriverActionsPanel from "@/components/DriverActionsPanel";
import { useTenantBranding, TenantLogo } from "@/components/TenantBrand";

function NoIndexHead() {
  useEffect(() => {
    // Set a noindex meta tag while this page is mounted.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Site info · timeslot.au";
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);
  return null;
}

export default function DriverSiteInfo() {
  const { token } = useParams();
  const [state, setState] = useState({
    loading: true, err: null,
    booking: null, site: null, rules: null, sections: [],
  });
  const brand = useTenantBranding({ booking_token: token });

  const reload = async () => {
    try {
      const { data, error } = await supabase.rpc("lbs_get_booking_site_info", { p_token: token });
      if (error) throw error;
      setState({
        loading: false,
        err: null,
        booking:  data?.booking  || null,
        site:     data?.site     || null,
        rules:    data?.rules    || null,
        sections: data?.sections || [],
      });
    } catch (e) {
      const msg = String(e?.message || "");
      setState((s) => ({
        ...s, loading: false,
        err: msg.includes("expired")
          ? "This link has expired — it stops working 24 hours after the booked slot ends."
          : "This booking link is no longer valid. The booking may have been cancelled or removed — please contact the site.",
      }));
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await reload();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload is recreated each render; run per token
  }, [token]);

  const { loading, err, booking, site, rules, sections } = state;

  const share = async () => {
    const url = window.location.href;
    const title = `Site info — ${site?.name || "booking"}`;
    if (navigator.share) {
      try { await navigator.share({ title, url }); return; } catch (_) { /* fall through */ }
    }
    await navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const mailto = () => {
    const url  = window.location.href;
    const body = `Site map & rules for booking ${booking?.reference_number || ""}:\n\n${url}\n`;
    window.location.href = `mailto:?subject=${encodeURIComponent(`Site info — ${site?.name || ""}`)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="driver-site-info">
      <NoIndexHead />
      <div className="max-w-3xl mx-auto">
        <TenantLogo brand={brand} className="mb-4" />
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-500 mb-4">
          <div className="flex items-center gap-2">
            <Truck className="h-4 w-4 text-blue-600" />
            <span className="font-semibold text-slate-900">timeslot.au</span>
            <span>·</span>
            <span>Driver site info</span>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="driver-print">
              <Printer className="h-4 w-4 mr-1.5" /> Print
            </Button>
            <Button size="sm" variant="outline" onClick={mailto} data-testid="driver-email">
              <Mail className="h-4 w-4 mr-1.5" /> Email
            </Button>
            <Button size="sm" onClick={share} data-testid="driver-share">
              <Share2 className="h-4 w-4 mr-1.5" /> Share link
            </Button>
          </div>
        </div>

        {loading && (
          <Card><CardContent className="py-10 text-center text-slate-500">Loading site information…</CardContent></Card>
        )}

        {!loading && err && (
          <Card><CardContent className="py-10 text-center text-rose-600 flex items-center justify-center gap-2">
            <AlertCircle className="h-4 w-4" /> {err}
          </CardContent></Card>
        )}

        {!loading && !err && site && (
          <div className="space-y-4">
            <Card>
              <CardContent className="py-6 space-y-4">
                <div>
                  <h1 className="text-2xl font-semibold tracking-tight">{site.name}</h1>
                  <div className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" />
                    {[site.address_line1, site.city, site.state].filter(Boolean).join(", ")}
                  </div>
                </div>

                {booking && (
                  <div className="rounded-md bg-slate-100 px-3 py-2 text-sm grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <div className="text-[10px] uppercase text-slate-500">Booking</div>
                      <div className="font-mono text-xs">{booking.reference_number || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-slate-500">Direction</div>
                      <div className="text-xs capitalize">{booking.direction || "—"}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[10px] uppercase text-slate-500">Scheduled</div>
                      <div className="text-xs">
                        <DriverSiteTime start={booking.scheduled_start} end={booking.scheduled_end} site={site} testId="site-info-scheduled" />
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {(site.operating_hours_start || site.operating_hours_end) && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <Clock className="h-4 w-4 text-slate-400" />
                      <span>
                        Hours:{" "}
                        <strong>{(site.operating_hours_start || "—").slice(0, 5)} – {(site.operating_hours_end || "—").slice(0, 5)}</strong>{" "}
                        ({placeTimeLabel(site, booking?.scheduled_start)})
                      </span>
                    </div>
                  )}
                  {site.contact_email && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <Mail className="h-4 w-4 text-slate-400" />
                      <a href={`mailto:${site.contact_email}`} className="hover:underline">{site.contact_email}</a>
                    </div>
                  )}
                  {site.contact_phone && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <Phone className="h-4 w-4 text-slate-400" />
                      <a href={`tel:${site.contact_phone}`} className="hover:underline">{site.contact_phone}</a>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Phase F ε — driver self-service edit panel, right under the booking
                details. The key prop re-mounts it after a reload so inputs re-hydrate. */}
            {booking && (
              <DriverActionsPanel
                key={`${booking.arrival_eta_at || ""}|${booking.driver_phone || ""}|${booking.site_rules_accepted_at || ""}`}
                token={token}
                booking={booking}
                tz={siteTz(site)}
                tzLabel={site ? placeTimeLabel(site, booking.scheduled_start) : undefined}
                onUpdated={() => reload()}
              />
            )}

            {rules?.intro_markdown && (
              <Card><CardContent className="py-6">
                <div className="text-sm whitespace-pre-wrap text-slate-700">{rules.intro_markdown}</div>
              </CardContent></Card>
            )}

            {sections.length > 0 && (
              <Card>
                <CardContent className="py-6 space-y-6">
                  <h2 className="text-lg font-semibold">Site rules &amp; site map</h2>
                  {sections.map((sec) => (
                    <div key={sec.id} className={sec.is_internal ? "" : ""}>
                      <SectionRenderer section={sec} />
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {sections.length === 0 && !rules?.intro_markdown && (
              <Card>
                <CardContent className="py-6 text-sm text-slate-500">
                  No site-specific rules have been published yet. Please contact the site directly.
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <p className="text-center text-xs text-slate-400 mt-6">
          Private link · do not share publicly · Powered by timeslot.au
        </p>
      </div>
    </div>
  );
}
